import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { updatePartner } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormBasic } from 'clickNgo/entities/partner/models/form/basic'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import {
  resetAllModifiedDataByKey,
  resetModifiedDataByField,
  useGetFormData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { TForm } from 'cng-common/features/form/models'
import Logger from 'core/utils/Logger'
import { showError, showSuccess } from 'onboarding/classes/services/notification'
import { convertErrorMessagesToHTMLList } from 'onboarding/utils/convertErrorMessagesToHTMLList'
import { cleanPropIfString } from 'onboarding/utils/object'

import { PartnerDataFormKeys, PartnerDataFormSettings } from '../../../configs'
import {
  PORTAL_PARTNER_STALE_DATA_MESSAGE,
  PORTAL_PARTNER_STATUS_CHECK_FAILED_MESSAGE,
  preparePartnerForSave,
} from '../../../helpers/preparePartnerForSave'
import { IPartnerDataFormData } from '../../../models'
import { PARTNER_BASIC_INFO_EDITABLE_FIELDS } from '../configs'

interface IUseEditPartnerBasicInfoHandlerParams {
  partner: IPartner
}

export const useEditPartnerBasicInfoHandler = ({
  partner,
}: IUseEditPartnerBasicInfoHandlerParams) => {
  const dispatch = useAppDispatch()
  const [actualData, updateActualData] = useGetFormDataField<IPartnerDataFormData>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.ActualData
  )
  const [_, handleBasicChange] = useGetFormData<IPartner>(PartnerDataFormKeys.BasicInfo)

  const [doCopyBasicAddress] = useGetFormDataField<boolean>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.DoCopyBasicAddress
  )

  const handleOnPartnerBasicChangesApply = useCallback(
    async ({ formData, invalidFields, modifiedFields }: TForm<IPartnerFormBasic>) => {
      const hasInvalidModifiedFields = Object.keys(invalidFields).some((invalidField) =>
        modifiedFields.includes(invalidField)
      )

      if (hasInvalidModifiedFields) {
        Logger.error('HAS INVALID FIELDS')
        return false
      }

      let updatedPartnerData: IPartner = {
        ...partner,
        ...formData,
        modifiedValues: modifiedFields,
      }

      if (doCopyBasicAddress) {
        updatedPartnerData = {
          ...updatedPartnerData,
          legalAddressLine1: updatedPartnerData.addressLine1,
          legalAddressLine2: updatedPartnerData.addressLine2,
          legalAddressLine3: updatedPartnerData.addressLine3,
          legalCity: updatedPartnerData.city,
          legalCountry: updatedPartnerData.country,
          legalState: updatedPartnerData.state,
          legalZipCode: updatedPartnerData.zipCode,
          modifiedValues: [
            ...updatedPartnerData.modifiedValues,
            'LegalAddressLine1',
            'LegalAddressLine2',
            'LegalAddressLine3',
            'LegalCity',
            'LegalCountry',
            'LegalState',
            'LegalZipCode',
          ],
        }
      }
      cleanPropIfString(updatedPartnerData)

      let partnerForSave: IPartner

      try {
        const preparationResult = await preparePartnerForSave(updatedPartnerData)

        if (preparationResult.status === 'stale') {
          showError(PORTAL_PARTNER_STALE_DATA_MESSAGE, { autoHide: false })
          return false
        }

        partnerForSave = preparationResult.partner
      } catch (error) {
        Logger.error(error)
        showError(PORTAL_PARTNER_STATUS_CHECK_FAILED_MESSAGE, { autoHide: false })
        return false
      }

      const result = await updatePartner(partnerForSave)

      if (result.status === OnboardingResponseStatus.Success) {
        updateActualData({
          ...actualData,
          partner: {
            ...partnerForSave,
            ...result.data,
          },
        })
        PARTNER_BASIC_INFO_EDITABLE_FIELDS.forEach((field) => {
          const value = partnerForSave[field]
          handleBasicChange({ field: field as keyof IPartner, value })
        })
        showSuccess('Partner data is saved')

        return true
      }

      if (result.status === OnboardingResponseStatus.Fail) {
        showError(convertErrorMessagesToHTMLList(getJSendErrorMessages(result)), {
          autoHide: false,
        })
      } else {
        showError(result.message, { autoHide: false })
      }

      return false
    },
    [actualData, doCopyBasicAddress, handleBasicChange, partner, updateActualData]
  )

  const handleOnPartnerBasicChangesCancel = useCallback(
    ({ modifiedFields }: TForm<IPartnerFormBasic>) => {
      modifiedFields.forEach((field) => {
        dispatch(resetModifiedDataByField({ formKey: PartnerDataFormKeys.BasicInfo, field }))
      })

      dispatch(resetAllModifiedDataByKey(PartnerDataFormKeys.Setting))
    },
    [dispatch]
  )

  return { handleOnPartnerBasicChangesApply, handleOnPartnerBasicChangesCancel }
}
